import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Check, GripVertical, Pencil, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CompetencyBadge } from './competency-badge';
import type { WizardQuestion } from './interviews.types';
import { ScoringExamplesPanel } from './scoring-examples-panel';

import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Textarea } from '~/components/ui/textarea';

type QuestionCardProps = {
  id: string;
  question: WizardQuestion;
  onUpdate: (updated: WizardQuestion) => void;
  onDelete: () => void;
};

export function QuestionCard({
  id,
  question,
  onUpdate,
  onDelete
}: QuestionCardProps): React.ReactNode {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(question.text);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  function saveEdit(): void {
    if (editText.trim()) {
      onUpdate({ ...question, text: editText.trim() });
    }
    setEditing(false);
  }

  function cancelEdit(): void {
    setEditText(question.text);
    setEditing(false);
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group flex items-start gap-2 border-b border-border py-2 last:border-b-0 sm:gap-3 sm:rounded-md sm:border sm:bg-card sm:p-3 sm:shadow-sm"
    >
      <button
        type="button"
        className="mt-0.5 hidden shrink-0 cursor-grab touch-none opacity-0 group-hover:opacity-100 sm:flex"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-4 text-muted-foreground" />
      </button>

      <div className="min-w-0 flex-1">
        {editing ? (
          <Textarea
            value={editText}
            onChange={(e) => {
              setEditText(e.target.value);
            }}
            className="min-h-[60px] text-sm"
            autoFocus
          />
        ) : (
          <p className="text-sm text-foreground">{question.text}</p>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-2">
          {question.aiGenerated && (
            <span className="text-xs text-muted-foreground">
              {t('wizard.questions.aiGenerated')}
            </span>
          )}
          {question.isOptional && (
            <Badge variant="outline" className="text-xs">
              {t('wizard.questions.optional')}
            </Badge>
          )}
          {question.competencyName && <CompetencyBadge name={question.competencyName} />}
        </div>

        {question.scoringExamples && (
          <ScoringExamplesPanel
            score1={question.scoringExamples.score1}
            score3={question.scoringExamples.score3}
            score5={question.scoringExamples.score5}
          />
        )}
      </div>

      {editing ? (
        <div className="flex shrink-0 flex-col gap-1 sm:flex-row">
          <Button variant="ghost" size="icon-sm" onClick={saveEdit}>
            <Check className="size-4 text-primary" />
          </Button>
          <Button variant="ghost" size="icon-sm" onClick={cancelEdit}>
            <X className="size-4 text-muted-foreground" />
          </Button>
        </div>
      ) : (
        <div className="flex shrink-0 flex-col gap-1 opacity-0 group-hover:opacity-100 sm:flex-row">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => {
              setEditing(true);
            }}
          >
            <Pencil className="size-4 text-muted-foreground" />
          </Button>
          <Button variant="ghost" size="icon-sm" onClick={onDelete}>
            <Trash2 className="size-4 text-destructive" />
          </Button>
        </div>
      )}
    </div>
  );
}
